import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Upload, CheckCircle, AlertCircle, Calendar, User, Phone, Mail, MapPin, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Layout from '../components/layout/Layout';
import { programmes, departments, regions } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Apply = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    gender: '',
    dateOfBirth: '',
    phoneNumber: '',
    email: '',
    region: '',
    district: '',
    ward: '',
    // Programme Selection
    programmeId: '',
    intake: '',
    // Education Background
    educationBackground: '',
    // File uploads (placeholder)
    idDocument: null,
    certificates: null,
    passportPhoto: null,
  });

  const selectedProgramme = programmes.find((p) => p.id === formData.programmeId);
  const availableIntakes = selectedProgramme?.intakes || [];

  // Reset intake if programme changes and current intake is not available
  useEffect(() => {
    if (formData.intake && !availableIntakes.includes(formData.intake)) {
      setFormData((prev) => ({ ...prev, intake: '' }));
    }
  }, [formData.programmeId, availableIntakes, formData.intake]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
      if (!formData.gender) newErrors.gender = 'Gender is required';
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.region) newErrors.region = 'Region is required';
    }

    if (step === 2) {
      if (!formData.programmeId) newErrors.programmeId = 'Please select a programme';
      if (!formData.intake) newErrors.intake = 'Please select an intake';
    }

    if (step === 3) {
      if (!formData.educationBackground.trim()) {
        newErrors.educationBackground = 'Education background is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          gender: formData.gender,
          dateOfBirth: formData.dateOfBirth,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          region: formData.region,
          district: formData.district,
          ward: formData.ward,
          programmeId: formData.programmeId,
          intake: formData.intake,
          educationBackground: formData.educationBackground,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit application');
      }

      setSubmissionResult(data);
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Personal Info', icon: User },
    { number: 2, title: 'Programme', icon: BookOpen },
    { number: 3, title: 'Education', icon: Calendar },
    { number: 4, title: 'Documents', icon: Upload },
  ];

  if (isSubmitted && submissionResult) {
    return (
      <Layout>
        <section className="section-padding">
          <div className="container-custom max-w-2xl">
            <Card className="border-none shadow-xl text-center">
              <CardContent className="py-12">
                <div className="w-20 h-20 bg-[#22c55e]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-[#22c55e]" />
                </div>
                <h1 className="font-display text-3xl font-bold text-[#1e3a5f] mb-4">
                  Application Submitted Successfully!
                </h1>
                <p className="text-[#475569] mb-2">
                  Thank you for applying to <strong>Baobab Institute of Tanzania</strong>.
                </p>
                <p className="text-[#475569] mb-4">
                  Your application for <strong>{selectedProgramme?.name}</strong> ({formData.intake} Intake) has been received.
                </p>
                <div className="bg-[#f8fafc] rounded-lg p-4 mb-6">
                  <p className="text-[#1e3a5f] font-semibold text-lg">
                    Application Number: {submissionResult.applicationNumber}
                  </p>
                  <p className="text-[#475569] text-sm mt-1">
                    Please save this number for future reference
                  </p>
                </div>
                <p className="text-[#475569] mb-6">
                  We will review your application and contact you within 2-4 weeks at <strong>{formData.email}</strong>
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/">
                    <Button className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white">
                      Return Home
                    </Button>
                  </Link>
                  <Link to="/programmes">
                    <Button variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]">
                      View Other Programmes
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </Layout>
    );
  }

  // Original success display as fallback
  if (isSubmitted) {
    return (
      <Layout>
        <section className="section-padding">
          <div className="container-custom max-w-2xl">
            <Card className="border-none shadow-xl text-center">
              <CardContent className="py-12">
                <div className="w-20 h-20 bg-[#22c55e]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-[#22c55e]" />
                </div>
                <h1 className="font-display text-3xl font-bold text-[#1e3a5f] mb-4">
                  Application Submitted Successfully!
                </h1>
                <p className="text-[#475569] mb-2">
                  Thank you for applying to <strong>Baobab Institute of Tanzania</strong>.
                </p>
                <p className="text-[#475569] mb-6">
                  Your application for <strong>{selectedProgramme?.name}</strong> ({formData.intake} Intake) has been received.
                  We will review your application and contact you within 2-4 weeks.
                </p>
                <div className="bg-[#f8fafc] rounded-lg p-4 mb-6">
                  <p className="text-sm text-[#475569]">
                    A confirmation email has been sent to <strong>{formData.email}</strong>
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/">
                    <Button className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white">
                      Return Home
                    </Button>
                  </Link>
                  <Link to="/programmes">
                    <Button variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]">
                      View Other Programmes
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="bg-[#1e3a5f] py-10">
        <div className="container-custom">
          <Link
            to="/admissions"
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admissions
          </Link>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-2">
            Application Form
          </h1>
          <p className="text-white/80">
            Complete the form below to apply for admission at BIT.
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="bg-[#f8fafc] py-6 border-b border-[#e2e8f0]">
        <div className="container-custom">
          <div className="flex justify-between max-w-3xl mx-auto">
            {steps.map((step) => (
              <div key={step.number} className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${
                    currentStep >= step.number
                      ? 'bg-[#1e3a5f] text-white'
                      : 'bg-[#e2e8f0] text-[#94a3b8]'
                  }`}
                >
                  {currentStep > step.number ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span
                  className={`text-xs font-medium ${
                    currentStep >= step.number ? 'text-[#1e3a5f]' : 'text-[#94a3b8]'
                  }`}
                >
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section-padding">
        <div className="container-custom max-w-3xl">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <Card className="border-none shadow-lg animate-fade-in">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-[#1e3a5f] flex items-center gap-2">
                    <User className="w-5 h-5 text-[#d4a855]" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name as it appears on your ID"
                      className={errors.fullName ? 'border-red-500' : ''}
                    />
                    {errors.fullName && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.fullName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Gender *</Label>
                    <RadioGroup
                      value={formData.gender}
                      onValueChange={(value) => handleSelectChange('gender', value)}
                      className="flex gap-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="male" id="male" />
                        <Label htmlFor="male" className="font-normal">Male</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="female" id="female" />
                        <Label htmlFor="female" className="font-normal">Female</Label>
                      </div>
                    </RadioGroup>
                    {errors.gender && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.gender}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                    <Input
                      id="dateOfBirth"
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className={errors.dateOfBirth ? 'border-red-500' : ''}
                    />
                    {errors.dateOfBirth && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.dateOfBirth}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phoneNumber">Phone Number *</Label>
                      <Input
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        placeholder="+255 XXX XXX XXX"
                        className={errors.phoneNumber ? 'border-red-500' : ''}
                      />
                      {errors.phoneNumber && (
                        <p className="text-red-500 text-sm flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.phoneNumber}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label>Region *</Label>
                      <Select
                        value={formData.region}
                        onValueChange={(value) => handleSelectChange('region', value)}
                      >
                        <SelectTrigger className={errors.region ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select region" />
                        </SelectTrigger>
                        <SelectContent>
                          {regions.map((region) => (
                            <SelectItem key={region} value={region}>
                              {region}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.region && (
                        <p className="text-red-500 text-sm flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.region}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="district">District</Label>
                      <Input
                        id="district"
                        name="district"
                        value={formData.district}
                        onChange={handleChange}
                        placeholder="Your district"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ward">Ward</Label>
                      <Input
                        id="ward"
                        name="ward"
                        value={formData.ward}
                        onChange={handleChange}
                        placeholder="Your ward"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Programme Selection */}
            {currentStep === 2 && (
              <Card className="border-none shadow-lg animate-fade-in">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-[#1e3a5f] flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-[#d4a855]" />
                    Programme Selection
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Select Programme *</Label>
                    <Select
                      value={formData.programmeId}
                      onValueChange={(value) => handleSelectChange('programmeId', value)}
                    >
                      <SelectTrigger className={errors.programmeId ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Choose your programme" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map((dept) => (
                          <React.Fragment key={dept.id}>
                            <SelectItem value={`dept-${dept.id}`} disabled className="font-semibold text-[#1e3a5f]">
                              {dept.shortName}
                            </SelectItem>
                            {programmes
                              .filter((p) => p.departmentId === dept.id)
                              .map((programme) => (
                                <SelectItem key={programme.id} value={programme.id}>
                                  {programme.name} (NTA {programme.ntaLevels.join('-')})
                                </SelectItem>
                              ))}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.programmeId && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.programmeId}
                      </p>
                    )}
                  </div>

                  {selectedProgramme && (
                    <div className="bg-[#f8fafc] rounded-lg p-4">
                      <h4 className="font-semibold text-[#1e3a5f] mb-2">{selectedProgramme.name}</h4>
                      <p className="text-[#475569] text-sm mb-2">{selectedProgramme.overview}</p>
                      <div className="flex gap-4 text-sm">
                        <span className="text-[#d4a855]">Duration: {selectedProgramme.duration}</span>
                        <span className="text-[#d4a855]">NTA Level: {selectedProgramme.ntaLevels.join('-')}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Select Intake *</Label>
                    <Select
                      value={formData.intake}
                      onValueChange={(value) => handleSelectChange('intake', value)}
                      disabled={!formData.programmeId}
                    >
                      <SelectTrigger className={errors.intake ? 'border-red-500' : ''}>
                        <SelectValue placeholder={formData.programmeId ? 'Choose intake' : 'Select programme first'} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableIntakes.map((intake) => (
                          <SelectItem key={intake} value={intake}>
                            {intake} Intake
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.intake && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.intake}
                      </p>
                    )}
                  </div>

                  {availableIntakes.length === 1 && availableIntakes[0] === 'September' && (
                    <div className="bg-[#fef3c7] border border-[#d4a855] rounded-lg p-4">
                      <p className="text-[#92400e] text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        This programme is only available during the September intake.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 3: Education Background */}
            {currentStep === 3 && (
              <Card className="border-none shadow-lg animate-fade-in">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-[#1e3a5f] flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#d4a855]" />
                    Education Background
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="educationBackground">Education History *</Label>
                    <Textarea
                      id="educationBackground"
                      name="educationBackground"
                      value={formData.educationBackground}
                      onChange={handleChange}
                      placeholder="Provide details of your education history including:
- Secondary school attended and year of completion
- CSEE results (subjects and grades)
- Any other relevant qualifications"
                      rows={8}
                      className={errors.educationBackground ? 'border-red-500' : ''}
                    />
                    {errors.educationBackground && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.educationBackground}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Document Upload */}
            {currentStep === 4 && (
              <Card className="border-none shadow-lg animate-fade-in">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-[#1e3a5f] flex items-center gap-2">
                    <Upload className="w-5 h-5 text-[#d4a855]" />
                    Document Upload
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-[#475569] text-sm">
                    Upload the required documents. Accepted formats: PDF, JPG, PNG (max 5MB each).
                  </p>

                  <div className="space-y-2">
                    <Label htmlFor="idDocument">National ID / Passport</Label>
                    <Input
                      id="idDocument"
                      name="idDocument"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      className="cursor-pointer"
                    />
                    {formData.idDocument && (
                      <p className="text-[#22c55e] text-sm flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {formData.idDocument.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="certificates">Academic Certificates (CSEE)</Label>
                    <Input
                      id="certificates"
                      name="certificates"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      className="cursor-pointer"
                    />
                    {formData.certificates && (
                      <p className="text-[#22c55e] text-sm flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {formData.certificates.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="passportPhoto">Passport Size Photo</Label>
                    <Input
                      id="passportPhoto"
                      name="passportPhoto"
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      className="cursor-pointer"
                    />
                    {formData.passportPhoto && (
                      <p className="text-[#22c55e] text-sm flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {formData.passportPhoto.name}
                      </p>
                    )}
                  </div>

                  <div className="bg-[#f8fafc] rounded-lg p-4">
                    <h4 className="font-semibold text-[#1e3a5f] mb-2">Application Summary</h4>
                    <div className="space-y-1 text-sm text-[#475569]">
                      <p><strong>Name:</strong> {formData.fullName}</p>
                      <p><strong>Programme:</strong> {selectedProgramme?.name}</p>
                      <p><strong>Intake:</strong> {formData.intake}</p>
                      <p><strong>Email:</strong> {formData.email}</p>
                    </div>
                  </div>

                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{submitError}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {currentStep > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="border-[#1e3a5f] text-[#1e3a5f]"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              ) : (
                <div />
              )}

              {currentStep < 4 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#d4a855] hover:bg-[#c49745] text-[#142942] font-semibold"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-[#142942]/30 border-t-[#142942] rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Submit Application
                    </span>
                  )}
                </Button>
              )}
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
};

export default Apply;
